import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";

const PaymentPlan = ({ uid }) => {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downPayment, setDownPayment] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  const months = ["May", "June", "July", "August", "September", "October", "November"];
  const monthlyPayment = 192.85;
  const taxRate = 0.0825;
  const targetTotal = 215.3;
  const subtotal = monthlyPayment;
  const tax = parseFloat((monthlyPayment * taxRate).toFixed(2));
  const stripeFee = parseFloat((targetTotal - subtotal - tax).toFixed(2));
  const total = targetTotal;

  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    if (!uid) return;
    const fetchReservation = async () => {
      try {
        const reservationRef = doc(db, "reservations", uid);
        const reservationSnap = await getDoc(reservationRef);
        if (reservationSnap.exists()) {
          const data = reservationSnap.data();
          const fetchedParticipants = data.participants || [];
          const participantsWithId = fetchedParticipants.map((p, index) => ({
            ...p,
            id: p.id || index + 1,
          }));
          setParticipants(participantsWithId);
          setDownPayment(data.downPayment || false);
        }
      } catch (error) {
        console.error("Error fetching reservation data:", error);
      }
      setLoading(false);
    };
    fetchReservation();
  }, [uid]);

  const markAsPaid = async (participantId, month) => {
    const updatedParticipants = participants.map((participant) => {
      if (participant.id === participantId) {
        return {
          ...participant,
          payments: {
            ...participant.payments,
            [month]: true,
          },
        };
      }
      return participant;
    });

    setParticipants(updatedParticipants);

    try {
      const reservationRef = doc(db, "reservations", uid);
      await updateDoc(reservationRef, { participants: updatedParticipants });
    } catch (error) {
      console.error("Error updating payment status in Firestore:", error);
    }
  };

  const handlePayment = async () => {
    if (!stripe || !elements || !selectedPayment) return;
    setPaymentLoading(true);
    try {
      const paymentData = {
        amount: Math.round(total * 100),
        uid,
        participant: {
          id: selectedPayment.participant.id,
          firstName: selectedPayment.participant.firstName,
          lastName: selectedPayment.participant.lastName,
        },
        month: selectedPayment.month,
      };

      const response = await fetch(
        "https://us-central1-newyorktour-33c8c.cloudfunctions.net/downPaymentApi/createPaymentIntent",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(paymentData),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        console.error("Payment Intent Error:", error);
        alert("Error creating Payment Intent.");
        setPaymentLoading(false);
        return;
      }

      const result = await response.json();
      const clientSecret = result.clientSecret;
      const cardElement = elements.getElement(CardElement);

      if (!clientSecret) {
        alert("Client Secret not received.");
        setPaymentLoading(false);
        return;
      }
      if (!cardElement) {
        alert("Card element not found.");
        setPaymentLoading(false);
        return;
      }

      const { paymentIntent, error } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: cardElement },
      });

      if (error) {
        console.error(error);
        alert("Payment failed.");
        setPaymentLoading(false);
        return;
      }

      if (paymentIntent.status === "succeeded") {
        await markAsPaid(selectedPayment.participant.id, selectedPayment.month);
        alert("Payment successful!");
        setSelectedPayment(null);
      }
    } catch (err) {
      console.error(err);
      alert("Payment error.");
    } finally {
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center p-4">Loading...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-6 bg-gray-50 rounded-lg shadow-lg relative">
      <h1 className="text-3xl font-bold mb-4 text-center text-indigo-800">Payment Plan</h1>
      <div className="overflow-x-auto">
        <table className="min-w-full table-auto border-collapse">
          <thead>
            <tr className="bg-gradient-to-r from-indigo-500 to-blue-500 text-white">
              <th className="border px-4 py-2">Participant</th>
              <th className="border px-4 py-2">Initial Payment</th>
              {months.map((month) => (
                <th key={month} className="border px-4 py-2">{month}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {participants.map((p) => (
              <tr key={p.id} className="text-center bg-white hover:bg-gray-100">
                <td className="border px-4 py-2 font-semibold text-gray-800">
                  {p.firstName} {p.lastName}
                </td>
                <td className="border px-4 py-2">
                  {downPayment ? (
                    <span className="text-green-600 font-bold">Paid ✅</span>
                  ) : (
                    <span className="text-red-600 font-bold">Pending ❗️</span>
                  )}
                </td>
                {months.map((month) => (
                  <td key={month} className="border px-4 py-2">
                    {p.payments && p.payments[month] ? (
                      <span className="text-green-600 font-bold">Paid</span>
                    ) : (
                      <motion.button
                        onClick={() => setSelectedPayment({ participant: p, month })}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-blue-600 text-white px-2 py-1 rounded text-xs"
                      >
                        Pay Now (${monthlyPayment})
                      </motion.button>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4 text-center text-indigo-800">Payment Receipt</h2>
            <div className="mb-4 text-gray-700">
              <p><strong>Participant:</strong> {selectedPayment.participant.firstName} {selectedPayment.participant.lastName}</p>
              <p><strong>Month:</strong> {selectedPayment.month}</p>
            </div>

            <div className="bg-gray-100 p-4 rounded mb-4 text-sm">
              <div className="flex justify-between mb-1">
                <span className="text-black">Base Payment:</span>
                <span className="text-black">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between mb-1">
                <span className="text-black">Tax (8.25%):</span>
                <span className="text-black">${tax.toFixed(2)}</span>
              </div>
              <div className="flex justify-between mb-1">
                <span className="text-black">Stripe Fee:</span>
                <span className="text-black">${stripeFee.toFixed(2)}</span>
              </div>
              <hr className="my-2" />
              <div className="flex justify-between font-bold">
                <span className="text-black">Total Charge:</span>
                <span className="text-black">${total.toFixed(2)}</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                You will be charged this amount to cover taxes & Stripe fees.
              </p>
            </div>

            <div className="bg-gray-100 p-4 rounded mb-4">
              <CardElement options={{ style: { base: { fontSize: "16px" } } }} />
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => setSelectedPayment(null)}
                className="bg-gray-400 text-white px-4 py-2 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handlePayment}
                disabled={paymentLoading}
                className="bg-blue-600 text-white px-4 py-2 rounded"
              >
                {paymentLoading ? "Processing..." : `Pay $${total.toFixed(2)}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentPlan;
